import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { TransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction/transaction.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import i18n from '@app/core/locale/i18n';
import { mapStateToGenericTransactionState } from '@app/feature/charge-point/helper/transaction-state.helper';
import { useCpmsBoGetTransactionQuery } from '@service/cpms-bo-transaction.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageBreadcrumbContext } from '@shared/layout/page-layout/page-layout-breadcrumb.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

import { TokenCardComponent } from './token-card/token-card.component';
import { TransactionCardComponent } from './transaction-card/transaction-card.component';
import { TransactionDetailsLoadingComponent } from './transaction-details-loading.component';
import * as css from './transaction-details.module.scss';
import { TypeCardComponent } from './type-card/type-card.component';

interface TransactionDetailsControllerProps {
  resource: TransactionModel;
}

const TransactionDetailsController = (
  props: TransactionDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // Nothing to do

  // ACTIONS ///////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(
      resource?.startedAt
        ? formatDate(resource?.startedAt, i18n)
        : t('$.transaction-details.title'),
    );
  }, [setPageTitle, resource, t]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    const { color, name, icon } = mapStateToGenericTransactionState(
      resource.state.state,
    );

    actions.push(
      <Tag className={classNames(color)} gap={GapEnum.XXS} key="state">
        <Icon name={icon.name} color={icon.color} />
        <Text>
          {t(`delmonicos-shared-sdk.enums-transaction-state.${name}`)}
        </Text>
      </Tag>,
    );

    setPageAction(actions);
  }, [resource, setPageAction, t]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  const setPageBreadcrumb = useSetPageBreadcrumbContext();

  useEffect(() => {
    if (!resource?.chargePoint?.id || !resource?.chargePoint?.name) {
      return;
    }

    setPageBreadcrumb([
      {
        label: resource.chargePoint.name,
        url: generatePath(APP_PATH.CHARGE_POINT.DETAILS, {
          id: resource.chargePoint.id,
        }),
        iconName: 'electric-terminal',
      },
    ]);
  }, [setPageBreadcrumb, resource, t]);

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.L} direction="column">
      <Stack gap={GapEnum.L} className={css.flexedItems}>
        <TypeCardComponent transaction={resource} />
        <TokenCardComponent transaction={resource} />
      </Stack>
      <TransactionCardComponent transaction={resource} />
    </Stack>
  );
};

export const TransactionDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetTransactionQuery({ id: params.id ?? '' });

  const { Component } = withProtectedResource<
    TransactionModel,
    {},
    TransactionModel
  >(TransactionDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.TRANSACTIONS,
        action: ActionsByResource.TRANSACTIONS.READ,
      }),
    loadingComponent: <TransactionDetailsLoadingComponent />,
  })({});

  return Component;
};
